import type { ReviewQueueSession, RoleplayLine, RoleplayScore } from "@/lib/training/trainingApiClient";
import { secondaryBtnClass } from "@/components/common/surfaceClasses";
import { roleplayScoreLine, scenarioLabel } from "./trainingReviewQueueShared";
import { TrainingReviewRubricGrid } from "./TrainingReviewRubricGrid";

type RubricState = {
  mGrounding: number;
  setMGrounding: (n: number) => void;
  mTone: number;
  setMTone: (n: number) => void;
  mAccuracy: number;
  setMAccuracy: (n: number) => void;
  lClarity: number;
  setLClarity: (n: number) => void;
  lAnswersCustomer: number;
  setLAnswersCustomer: (n: number) => void;
  lFollowUpQuestions: number;
  setLFollowUpQuestions: (n: number) => void;
  lOpenEndedQuestions: number;
  setLOpenEndedQuestions: (n: number) => void;
};

type Props = {
  session: ReviewQueueSession;
  expanded: boolean;
  onToggle: () => void;
  ai: RoleplayScore | null;
  lines: RoleplayLine[];
  submitErr: string | null;
  rubric: RubricState;
  note: string;
  setNote: (s: string) => void;
  finalizeArchive: boolean;
  setFinalizeArchive: (v: boolean) => void;
  submitting: boolean;
  onSubmitReview: () => void;
  onSkip: () => void;
};

export function TrainingReviewQueueListItem(props: Props) {
  const {
    session: s,
    expanded: openExpanded,
    onToggle,
    ai,
    lines,
    submitErr,
    rubric,
    note,
    setNote,
    finalizeArchive,
    setFinalizeArchive,
    submitting,
    onSubmitReview,
    onSkip,
  } = props;

  return (
    <li className="border border-gray-200 dark:border-gray-700 rounded-2xl bg-white dark:bg-gray-900 overflow-hidden">
      <button
        type="button"
        className="w-full text-left px-4 py-3 flex flex-wrap items-center justify-between gap-2 hover:bg-gray-50 dark:hover:bg-gray-800/70"
        onClick={onToggle}
      >
        <div>
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {s.traineeLabel ?? "Trainee"} · {scenarioLabel(s.scenario_key)}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {new Date(s.created_at).toLocaleString()} · AI overall {s.overall_score?.toFixed(1) ?? "—"}/10 · {lines.length} lines
          </div>
        </div>
        <span className="text-xs text-indigo-600 font-semibold">{openExpanded ? "Close" : "Review"}</span>
      </button>

      {openExpanded ? (
        <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-3 space-y-4 bg-gray-50/80 dark:bg-gray-800/70">
          {ai ? (
            <div className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
              <div className="font-semibold text-gray-800 dark:text-gray-100">AI session score (reference)</div>
              <div>{roleplayScoreLine(ai)}</div>
              <p className="text-sm text-gray-800 dark:text-gray-100 italic">{ai.summary ?? ""}</p>
            </div>
          ) : null}

          <div className="max-h-48 overflow-y-auto space-y-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-2">
            {lines.map((m, i) => (
              <div
                key={`${s.id}-${i}`}
                className={`text-sm rounded-lg px-2 py-1.5 ${
                  m.role === "customer"
                    ? "bg-slate-100 text-gray-900 dark:bg-slate-800 dark:text-slate-100"
                    : "bg-indigo-50 text-indigo-950 dark:bg-indigo-950/45 dark:text-indigo-100 ml-2"
                }`}
              >
                <span className="text-[10px] uppercase text-gray-500 dark:text-gray-400">
                  {m.role === "customer" ? "Customer" : "Trainee"}
                </span>
                <div className="whitespace-pre-wrap">{m.content}</div>
              </div>
            ))}
          </div>

          {submitErr ? (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-2 py-1">{submitErr}</div>
          ) : null}

          <p className="text-[11px] text-gray-600 dark:text-gray-300 leading-relaxed rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-2.5 py-2">
            <span className="font-medium text-gray-800 dark:text-gray-100">Coaching tip:</span> use the rubric for a quick read, then put the takeaway in
            the note — one or two specific behaviors to repeat or adjust lands better than numbers alone.
          </p>

          <TrainingReviewRubricGrid {...rubric} />

          <label className="inline-flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
            <input type="checkbox" checked={finalizeArchive} onChange={(e) => setFinalizeArchive(e.target.checked)} />
            After review, archive session (remove transcript, keep summary & scores for rollups)
          </label>

          <label className="block text-xs text-gray-700 dark:text-gray-300">
            Note (optional)
            <textarea
              className="mt-1 w-full rounded-lg border border-gray-200 text-sm px-2 py-1.5 min-h-[72px]"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Observations for KB, prompts, or coaching follow-up…"
              maxLength={4000}
            />
          </label>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={submitting}
              className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              onClick={onSubmitReview}
            >
              {submitting ? "Saving…" : "Submit review"}
            </button>
            <button
              type="button"
              disabled={submitting}
              className={`rounded-xl ${secondaryBtnClass} px-4 py-2 text-sm font-semibold disabled:opacity-50`}
              onClick={onSkip}
            >
              Skip
            </button>
          </div>
        </div>
      ) : null}
    </li>
  );
}
